import {isChunkLoadError, retryFailedLazyImports} from '@aboutyou/lazy-imports';
import {Log} from '@aboutyou/mlm';
import React, {ErrorInfo, PropsWithChildren, type JSX} from 'react';
import {ErrorPageInline} from '../../atoms/ErrorPageInline/ErrorPageInline';

interface ErrorDetails {
  error: Error | null;
  errorInfo: ErrorInfo | null;
  retried: boolean;
}

/**
 * A chunk that fails again within this time after an automatic remount is
 * treated as unrecoverable, so the error page is shown instead.
 */
const CHUNK_LOAD_ERROR_REMOUNT_COOLDOWN_MS = 5000;

export class ErrorBoundary extends React.Component<
  {
    ErrorComponent?: React.ComponentType<{children?: React.ReactNode}> | null;
    renderContext: string;
    children?: React.ReactNode;
  },
  ErrorDetails
> {
  state: ErrorDetails = {
    error: null,
    errorInfo: null,
    retried: false,
  };

  private lastChunkLoadErrorRemountAt = -Infinity;

  static getDerivedStateFromError(error: Error): Partial<ErrorDetails> {
    return {error};
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (this.shouldRemountAfterChunkLoadError(error)) {
      this.lastChunkLoadErrorRemountAt = Date.now();
      retryFailedLazyImports();
      this.setState({error: null, errorInfo: null});
      return;
    }

    this.setState({errorInfo});

    try {
      Log.error(
        '@aboutyou/ui-components',
        `Error Boundary rendered Oops component in context ${this.props.renderContext}`,
        {
          error,
          stackTrace: errorInfo?.componentStack?.substring(0, 150),
        },
      );
    } catch (loggingError) {
      // A throw while React is committing error handling would escalate to the
      // parent boundary instead of being reported here, so swallow it.
    }
  }

  resetError({isRetrying}: {isRetrying: boolean}) {
    retryFailedLazyImports();
    this.setState({error: null, errorInfo: null, retried: isRetrying});
  }

  private shouldRemountAfterChunkLoadError(error: Error) {
    return (
      isChunkLoadError(error) &&
      Date.now() - this.lastChunkLoadErrorRemountAt >
        CHUNK_LOAD_ERROR_REMOUNT_COOLDOWN_MS
    );
  }

  render() {
    const {error} = this.state;

    if (error && this.shouldRemountAfterChunkLoadError(error)) {
      return null;
    }

    return (
      <ErrorContainer
        {...this.props}
        {...this.state}
        resetError={this.resetError.bind(this)}
      />
    );
  }
}

interface ErrorContainerProps extends ErrorDetails {
  ErrorComponent?: React.ComponentType<React.PropsWithChildren> | null;
  renderContext: string;
  resetError: (options: {isRetrying: boolean}) => void;
}

function ErrorContainer({
  error,
  children,
  ErrorComponent,
  resetError,
  retried,
}: PropsWithChildren<ErrorContainerProps>) {
  if (!error) {
    return children as JSX.Element;
  }

  if (ErrorComponent) {
    return <ErrorComponent />;
  }

  if (ErrorComponent === null) {
    return null;
  }

  return (
    <DefaultErrorComponent
      error={error}
      resetError={resetError}
      retried={retried}
    />
  );
}

const DefaultErrorComponent: React.FC<{
  error: Error;
  resetError: (options: {isRetrying: boolean}) => void;
  retried?: boolean;
}> = ({error, resetError, retried}) => {
  return (
    <ErrorPageInline
      subline={process.env.NODE_ENV !== 'production' && error.message}
      resetError={resetError}
      retried={retried}
    />
  );
};
